import type { FC } from 'react';

import classNames from 'classnames';

import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { getTitleFromDomain } from '@iconik-io/shared-lib/src/utils/get_title_from_domain';
import { MEDIA_TYPE_AUDIO } from '@iconik-io/shared-lib/src/utils/media_type';
import { rangeToSmpteString } from '@iconik-io/shared-lib/src/utils/range_to_smpte_string';
import { Icon } from '@iconik-io/uad';

import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { selectKeyframesUpdate } from '../../ducks/search/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { ui } from '../../utils/helpers';
import { getSearchItemMediaType } from '../../utils/media_type';
import ApprovalDetailedStatusIcon from '../asset_approval_status_detailed/asset_approval_status_detailed';
import DetailedStatusIcon from '../asset_status_icon_detailed/asset_status_icon_detailed';
import DebugDumpEntity from '../debug_dump_entity/debug_dump_entity';
import { ExternalLink } from '../external_link/external_link';
import { Keyframes } from '../keyframes/keyframes';
import PodAudioPlayer from '../pod_audio_player/pod_audio_player';
import SearchItemProgress from '../search_items/search_item_progress/search_item_progress';
import { getStateClass } from '../search_items/search_items_pods_list/utils';
import MetadataRow from './metadata_row';
import SearchPodTitle from './search_pod_title';
import type { SearchPodProps } from './types';

import progressStyles from '../search_items/search_item_progress/search_item_progress.module.css';
import styles from './search_pod_asset_detailed.module.css';

const SearchPodAssetDetailed: FC<SearchPodProps> = (props) => {
  const {
    searchItem,
    isDragging,
    audioPlayer,
    imageScrub,
    hideIcons,
    getObjectLink,
    onTitleLinkClick,
    assetMetadataView,
    podSize = { height: 146, width: 262 },
    hideContextMenu = false,
    hideFavoriteButton,
    hasPodTitle = true,
    loading,
  } = props;

  const isDisabled = searchItem.status === 'DELETED';
  const isSelected = Boolean(props.isSelected && !isDisabled);
  const isDropTarget = props.isDropTarget && searchItem.type !== 'LINK';
  const dispatch = useAppDispatch();

  const keyframesUpdate = useAppSelector(selectKeyframesUpdate);
  const dragAndDropWaiting = useAppSelector((state) =>
    state.dragAndDropKeyframes.waiting.includes(searchItem.id),
  );
  const waiting =
    keyframesUpdate?.[searchItem.id]?.loading ?? dragAndDropWaiting;

  const isTranscoded = (searchItem.proxies?.length ?? 0) > 0;

  const onLoadAudioUrlClick = (): void => {
    const { setPodAudioPlayerAsset, getAssetProxyFileUrlById } = props;
    const { id: assetId, proxies } = searchItem;

    if (proxies && setPodAudioPlayerAsset) {
      const { id } = proxies[0];

      setPodAudioPlayerAsset(assetId);
      if (!audioPlayer?.audioProxies[assetId] && getAssetProxyFileUrlById) {
        getAssetProxyFileUrlById(assetId, id);
      }
    }
  };

  const stateClass = getStateClass({
    isSelected,
    isDragging,
    isDropTarget,
    status: searchItem.status,
  });

  const { metadata } = searchItem;

  const titleFromDomain = getTitleFromDomain(searchItem?.external_link);
  const proxy = searchItem.proxies?.[0];
  let subclipTimecode;
  if (searchItem.type === 'SUBCLIP' && proxy) {
    const timecodeOptions = getProxyTimecodeOptions(proxy);
    subclipTimecode = rangeToSmpteString({
      startMilliseconds: searchItem.time_start_milliseconds,
      endMilliseconds: searchItem.time_end_milliseconds,
      timecodeOptions,
    });
  }

  if (loading) {
    return <LoadingBar />;
  }

  return (
    <div
      className={stateClass ? styles[stateClass] : undefined}
      draggable="true"
      {...ui('search-pod-asset')}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      aria-label={searchItem.title}
      tabIndex={0}
    >
      <DebugDumpEntity id={searchItem.id} entityKey="search_items" />
      <div>
        <div
          style={podSize ? { height: `${podSize.height}px` } : undefined}
          className={styles.pod_content}
        >
          {!hideIcons && (
            <div className={styles.icon_bar_right}>
              <ApprovalDetailedStatusIcon approval={searchItem.approval} />
              <div className={styles.detailed_status_container}>
                <DetailedStatusIcon object={searchItem} />
              </div>
            </div>
          )}

          {!hideIcons &&
            titleFromDomain &&
            searchItem.type === 'LINK' &&
            searchItem?.external_link && (
              <div className={styles.icon_bar_left}>
                <ExternalLink
                  href={searchItem?.external_link}
                  className={styles.external_link_container}
                  onOpened={() =>
                    dispatch(
                      segmentTrackEvent({
                        eventName: 'External Link Opened',
                      }),
                    )
                  }
                >
                  {titleFromDomain}
                  <Icon
                    name="open"
                    className={styles.external_link_icon}
                    size="xs"
                  />
                </ExternalLink>
              </div>
            )}

          {!hideIcons &&
          audioPlayer &&
          isTranscoded &&
          getSearchItemMediaType(searchItem) === MEDIA_TYPE_AUDIO ? (
            <div className={styles.player}>
              <PodAudioPlayer
                assetId={searchItem.id}
                onLoadAudioUrlClick={onLoadAudioUrlClick}
                audioPlayerStore={audioPlayer}
              />
            </div>
          ) : null}
          <Keyframes
            searchItem={searchItem}
            highResolution
            imageScrub={imageScrub}
          />
          {subclipTimecode || searchItem.warning ? (
            <div className={styles.bottom_labels}>
              {subclipTimecode && (
                <div className={classNames(styles.timecode)}>
                  {subclipTimecode}
                </div>
              )}

              {searchItem.warning && (
                <div className={styles.warning} title={searchItem.warning}>
                  <Icon name="message_warning" size="xs" />
                  <span>{searchItem.warning}</span>
                </div>
              )}
            </div>
          ) : null}
          <div className={styles.status_bar}>
            <SearchItemProgress id={searchItem.id} />
          </div>
        </div>

        {waiting && (
          <div
            className={classNames(
              progressStyles.progress_line,
              'WAITING',
              styles.waiting,
            )}
          />
        )}
      </div>
      {hasPodTitle && (
        <div className={styles.details_container}>
          <div className={styles.title_container}>
            <SearchPodTitle
              searchItem={searchItem}
              objectType="asset"
              isSelected={isSelected}
              isDropTarget={isDropTarget}
              getObjectLink={getObjectLink}
              onTitleLinkClick={onTitleLinkClick}
              hideContextMenu={hideContextMenu}
              hideFavouriteButton={hideFavoriteButton}
            />
          </div>
          {podSize && podSize.width >= 308 && assetMetadataView ? (
            <div className={styles.metadata_container}>
              {assetMetadataView?.view_fields?.map((item, i) => {
                const value = metadata?.[item.name];

                return (
                  <MetadataRow
                    key={i}
                    field={item}
                    value={value as unknown[]}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchPodAssetDetailed;
